/**
 * Utility functions for generating consistent badge colors and styles
 */

/**
 * Converts hex color to Tailwind CSS classes using color proximity matching
 * @param hexColor - Hex color code (e.g., "#DC2626")
 * @returns Tailwind CSS classes for the badge
 */
export const hexToTailwindClasses = (hexColor: string): string => {
  // Remove # if present
  const hex = hexColor.replace('#', '');
  
  // Convert hex to RGB
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  
  // Define color ranges with their Tailwind classes
  const colorRanges = [
    { name: 'red', classes: 'bg-red-100 text-red-800', ranges: [{ min: [200, 0, 0], max: [255, 100, 100] }] },
    { name: 'orange', classes: 'bg-orange-100 text-orange-800', ranges: [{ min: [200, 100, 0], max: [255, 200, 50] }] },
    { name: 'yellow', classes: 'bg-yellow-100 text-yellow-800', ranges: [{ min: [200, 200, 0], max: [255, 255, 100] }] },
    { name: 'green', classes: 'bg-green-100 text-green-800', ranges: [{ min: [0, 150, 0], max: [100, 255, 100] }] },
    { name: 'blue', classes: 'bg-accent-color-100 text-accent-color-800', ranges: [{ min: [0, 0, 150], max: [100, 100, 255] }] },
    { name: 'purple', classes: 'bg-purple-100 text-purple-800', ranges: [{ min: [100, 0, 150], max: [200, 100, 255] }] },
    { name: 'pink', classes: 'bg-pink-100 text-pink-800', ranges: [{ min: [200, 100, 150], max: [255, 200, 255] }] },
    { name: 'indigo', classes: 'bg-indigo-100 text-indigo-800', ranges: [{ min: [50, 0, 150], max: [150, 100, 255] }] },
    { name: 'cyan', classes: 'bg-cyan-100 text-cyan-800', ranges: [{ min: [0, 150, 150], max: [100, 255, 255] }] },
    { name: 'teal', classes: 'bg-teal-100 text-teal-800', ranges: [{ min: [0, 100, 100], max: [100, 200, 200] }] },
    { name: 'emerald', classes: 'bg-emerald-100 text-emerald-800', ranges: [{ min: [0, 200, 100], max: [100, 255, 150] }] },
    { name: 'lime', classes: 'bg-lime-100 text-lime-800', ranges: [{ min: [150, 200, 0], max: [200, 255, 100] }] },
    { name: 'amber', classes: 'bg-amber-100 text-amber-800', ranges: [{ min: [200, 150, 0], max: [255, 200, 50] }] },
    { name: 'gray', classes: 'bg-gray-100 text-gray-800', ranges: [{ min: [100, 100, 100], max: [200, 200, 200] }] },
  ];
  
  // Find the closest color range
  for (const colorRange of colorRanges) {
    for (const range of colorRange.ranges) {
      if (r >= range.min[0] && r <= range.max[0] &&
          g >= range.min[1] && g <= range.max[1] &&
          b >= range.min[2] && b <= range.max[2]) {
        return colorRange.classes;
      }
    }
  }
  
  // Default fallback
  return 'bg-gray-100 text-gray-800';
};

/**
 * Gets the CSS classes for priority badge colors from database
 * @param priority - The priority object with colorCode
 * @returns CSS classes for the priority badge
 */
export const getPriorityBadgeClasses = (priority: { colorCode?: string }): string => {
  return priority.colorCode ? hexToTailwindClasses(priority.colorCode) : 'bg-gray-100 text-gray-800';
};

/**
 * Gets the CSS classes for priority badge colors by name (fallback for legacy usage)
 * @param priorityName - The priority name (case insensitive)
 * @returns CSS classes for the priority badge
 */
export const getPriorityBadgeClassesByName = (priorityName: string): string => {
  switch (priorityName.toLowerCase()) {
    case 'critical':
    case 'p0':
      return 'bg-red-100 text-red-800';
    case 'high':
    case 'p1':
      return 'bg-orange-100 text-orange-800';
    case 'medium':
    case 'p2':
      return 'bg-yellow-100 text-yellow-800';
    case 'low':
    case 'p3':
      return 'bg-green-100 text-green-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

/**
 * Gets the CSS classes for status badge colors
 * @param statusName - The status name (case insensitive)
 * @returns CSS classes for the status badge
 */
export const getStatusBadgeClasses = (statusName: string, badgeClass?: string): string => {
  if (badgeClass && badgeClass.trim().length > 0) {
    return badgeClass;
  }
  switch (statusName.toLowerCase()) {
    case 'open':
    case 'open (unassigned)':
      return 'bg-accent-color-100 text-accent-color-800';
    case 'in progress':
    case 'assigned':
      return 'bg-yellow-100 text-yellow-800';
    case 'on hold':
      return 'bg-orange-100 text-orange-800';
    case 'completed':
    case 'closed':
      return 'bg-green-100 text-green-800';
    case 'cancelled':
      return 'bg-red-100 text-red-800';
    case 'estimate received':
      return 'bg-purple-100 text-purple-800';
    case 'proposal sent to customer':
      return 'bg-indigo-100 text-indigo-800';
    case 'approved':
      return 'bg-emerald-100 text-emerald-800';
    case 'vendor notified of approval':
      return 'bg-teal-100 text-teal-800';
    case 'ready to invoice':
      return 'bg-cyan-100 text-cyan-800';
    case 'invoiced':
      return 'bg-accent-color-100 text-accent-color-800';
    case 'paid':
      return 'bg-green-100 text-green-800';
    case 'closed no changes':
      return 'bg-gray-100 text-gray-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

/**
 * Gets the CSS classes for invoice status badge colors
 * @param status - The invoice status (case insensitive)
 * @returns CSS classes for the invoice status badge
 */
export const getInvoiceStatusBadgeClasses = (status: string): string => {
  switch (status.toLowerCase()) {
    case 'draft':
      return 'bg-gray-100 text-gray-800';
    case 'sent':
      return 'bg-accent-color-100 text-accent-color-800';
    case 'paid':
      return 'bg-green-100 text-green-800';
    case 'overdue':
      return 'bg-red-100 text-red-800';
    case 'cancelled':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

/**
 * Gets the CSS classes for entry type badge colors
 * @param entryType - The entry type
 * @returns CSS classes for the entry type badge
 */
export const getEntryTypeBadgeClasses = (entryType: string): string => {
  switch (entryType) {
    case 'Note':
      return 'bg-accent-color-100 text-accent-color-800 border-accent-color-200';
    case 'StatusUpdate':
      return 'bg-green-100 text-green-800 border-green-200';
    case 'VendorMessage':
      return 'bg-purple-100 text-purple-800 border-purple-200';
    case 'ClientMessage':
      return 'bg-orange-100 text-orange-800 border-orange-200';
    case 'SystemMessage':
      return 'bg-gray-100 text-gray-800 border-gray-200';
    default:
      return 'bg-gray-100 text-gray-800 border-gray-200';
  }
};

/**
 * Gets priority colors for calendar events
 * @param priority - The priority name (case insensitive)
 * @returns Object with backgroundColor, borderColor, and textColor
 */
export const getPriorityColors = (priority: string) => {
  switch (priority.toLowerCase()) {
    case 'critical':
    case 'p0':
      return {
        backgroundColor: '#fef2f2',
        borderColor: '#dc2626',
        textColor: '#dc2626'
      };
    case 'high':
    case 'p1':
      return {
        backgroundColor: '#fef3c7',
        borderColor: '#d97706',
        textColor: '#d97706'
      };
    case 'medium':
    case 'p2':
      return {
        backgroundColor: '#dbeafe',
        borderColor: '#2563eb',
        textColor: '#2563eb'
      };
    case 'low':
    case 'p3':
      return {
        backgroundColor: '#f0fdf4',
        borderColor: '#16a34a',
        textColor: '#16a34a'
      };
    default:
      return {
        backgroundColor: '#f9fafb',
        borderColor: '#6b7280',
        textColor: '#6b7280'
      };
  }
};

/**
 * Gets the CSS classes for days remaining text colors
 * @param days - Number of days remaining
 * @param isOverdue - Whether the item is overdue
 * @returns CSS classes for the days remaining text
 */
export const getDaysRemainingClasses = (days: number, isOverdue: boolean): string => {
  if (isOverdue) return 'text-red-600 font-semibold';
  if (days === 0) return 'text-orange-600 font-semibold';
  if (days <= 2) return 'text-yellow-600';
  if (days <= 7) return 'text-accent-color';
  return 'text-green-600';
};

/**
 * Formats days remaining text
 * @param days - Number of days remaining
 * @param isOverdue - Whether the item is overdue
 * @returns Formatted text for days remaining
 */
export const formatDaysRemaining = (days: number, isOverdue: boolean): string => {
  if (isOverdue) return `${Math.abs(days)} days overdue`;
  if (days === 0) return 'Due today';
  if (days === 1) return '1 day left';
  return `${days} days left`;
};
